/**
 * InstallHelpModal — single, browser-aware install dialog.
 *
 * Replaces the old auto-prompting banner. Opened on demand by InstallAppButton
 * (or VersionStamp debug menu). Detects browser + OS and shows exactly one
 * clear instruction tailored to the user's environment — no more "did the
 * click work? where did it go?" guesswork.
 *
 * Layout: each platform branch shows the same 3-part structure:
 *   - Headline ("Install on Brave for desktop")
 *   - Visual step list (1, 2, 3) or a single "Install now" button when the
 *     OS-level dialog is actually fireable
 *   - Optional fallback hint for browsers where prompt() may be suppressed
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Download, Share, Plus, MousePointer, Menu, ArrowUp } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { promptInstall, subscribeDeferredPrompt } from '../lib/pwaInstall';

const detectPlatform = async () => {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || '';

  // iOS — Safari OR Chrome on iOS, both behave the same (only Share menu).
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  // iPadOS lies about its UA — claims macOS but has touch points.
  if (/Mac/i.test(ua) && (navigator.maxTouchPoints || 0) > 1) return 'ios';

  // Brave — async detection via the navigator.brave API. Falls back gracefully.
  try {
    if (navigator.brave?.isBrave && (await navigator.brave.isBrave())) {
      return /Android/i.test(ua) ? 'brave-android' : 'brave-desktop';
    }
  } catch { /* ignore */ }

  if (/Android/i.test(ua)) {
    if (/Firefox/i.test(ua)) return 'firefox-android';
    return 'android-chrome'; // Chrome, Samsung Internet, etc. — all support prompt()
  }

  if (/Firefox/i.test(ua)) return 'firefox';
  // Safari desktop (no Chrome/Chromium/CriOS in UA on macOS).
  if (/Safari/i.test(ua) && !/Chrome|Chromium|CriOS/i.test(ua) && /Mac/i.test(ua)) {
    return 'safari-mac';
  }
  // Edge identifies itself via "Edg/" — still Chromium-based, prompt() works.
  // Falls through to chrome-desktop default which is the right path.
  return 'chrome-desktop';
};

const Step = ({ n, children, testid }) => (
  <li className="flex gap-3 items-start" data-testid={testid}>
    <span className="shrink-0 w-6 h-6 rounded-full bg-violet-500/20 border border-violet-400/40 text-violet-200 text-xs font-semibold flex items-center justify-center mt-0.5">
      {n}
    </span>
    <span className="text-white/80 text-sm leading-relaxed">{children}</span>
  </li>
);

export const InstallHelpModal = ({ open, onOpenChange }) => {
  const [platform, setPlatform] = useState('unknown');
  const [hasDeferred, setHasDeferred] = useState(false);

  useEffect(() => {
    let cancelled = false;
    detectPlatform().then((p) => { if (!cancelled) setPlatform(p); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    return subscribeDeferredPrompt((p) => setHasDeferred(!!p));
  }, []);

  const handleNativeInstall = () => {
    // Synchronous — preserves user activation for the OS install dialog.
    const result = promptInstall();
    if (result !== null) {
      // Close optimistically; OS dialog is now on top of the modal anyway.
      onOpenChange(false);
    }
  };

  const headline = useMemo(() => {
    switch (platform) {
      case 'ios': return 'Add NeuroWave to your home screen';
      case 'safari-mac': return 'Install on Safari for Mac';
      case 'brave-desktop': return 'Install on Brave';
      case 'brave-android': return 'Install on Brave for Android';
      case 'firefox': return 'Firefox doesn’t install PWAs';
      case 'firefox-android': return 'Use Chrome or Brave on Android';
      case 'android-chrome': return 'Install NeuroWave';
      case 'chrome-desktop':
      default: return 'Install NeuroWave';
    }
  }, [platform]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0c0918] border-white/10 text-white max-w-md" data-testid="install-help-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-500/15 border border-violet-400/30 flex items-center justify-center shrink-0">
              <Download className="w-5 h-5 text-violet-300" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-white">{headline}</DialogTitle>
              <DialogDescription className="text-white/55 text-xs">
                Standalone app · works offline · no app store
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="pt-2">
          {/* Chrome desktop / Edge / generic Chromium with prompt() available. */}
          {(platform === 'chrome-desktop' || platform === 'android-chrome') && hasDeferred && (
            <div className="space-y-3" data-testid="install-help-native">
              <p className="text-white/70 text-sm">
                One-click install — your browser will ask for confirmation.
              </p>
              <Button
                onClick={handleNativeInstall}
                className="w-full bg-violet-500 hover:bg-violet-400 text-white gap-2"
                data-testid="install-help-native-btn"
              >
                <Download className="w-4 h-4" /> Install now
              </Button>
            </div>
          )}

          {/* Chrome desktop / Android-Chrome without captured prompt — explain
              the address bar. Rare on Chrome (prompt always fires), common on
              the second visit after dismissing the mini-infobar. */}
          {(platform === 'chrome-desktop' || platform === 'android-chrome') && !hasDeferred && (
            <ol className="space-y-3" data-testid="install-help-chrome-manual">
              <Step n="1">
                Click the install icon{' '}
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/10 text-white/90 text-xs">
                  <MousePointer className="w-3 h-3" /> ⊕
                </span>{' '}
                on the right side of your address bar.
              </Step>
              <Step n="2">
                Click <span className="text-white font-medium">Install</span> in the dialog that appears.
              </Step>
            </ol>
          )}

          {/* iOS — Share → Add to Home Screen. No JS API exists. */}
          {platform === 'ios' && (
            <ol className="space-y-3" data-testid="install-help-ios">
              <Step n="1">
                Tap the Share button{' '}
                <Share className="w-4 h-4 inline text-cyan-300 -mt-0.5" aria-label="Share" />{' '}
                at the bottom of your screen.
              </Step>
              <Step n="2">
                Scroll down and tap{' '}
                <span className="text-white font-medium">
                  <Plus className="w-3.5 h-3.5 inline text-cyan-300 -mt-0.5" /> Add to Home Screen
                </span>.
              </Step>
              <Step n="3">
                Tap <span className="text-white font-medium">Add</span> in the top-right corner.
              </Step>
            </ol>
          )}

          {/* Safari macOS — File menu → Add to Dock (Safari 17+). */}
          {platform === 'safari-mac' && (
            <ol className="space-y-3" data-testid="install-help-safari-mac">
              <Step n="1">
                Click the <span className="text-white font-medium">File</span> menu at the top of your screen.
              </Step>
              <Step n="2">
                Choose <span className="text-white font-medium">Add to Dock…</span>
              </Step>
              <Step n="3">
                Click <span className="text-white font-medium">Add</span>. NeuroWave will appear in your Dock and Applications folder.
              </Step>
              <li className="text-white/40 text-xs pl-9">
                Requires Safari 17 or later (macOS Sonoma).
              </li>
            </ol>
          )}

          {/* Brave desktop — address-bar icon is reliable; promptInstall may be
              silently suppressed so we lead with the address bar. */}
          {platform === 'brave-desktop' && (
            <ol className="space-y-3" data-testid="install-help-brave-desktop">
              <Step n="1">
                Look at the right side of your address bar for the install icon{' '}
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-white/10 text-white/90 text-xs">⊕</span>.
              </Step>
              <Step n="2">
                Click it, then click <span className="text-white font-medium">Install</span>.
              </Step>
              <li className="text-white/40 text-xs pl-9">
                Brave’s privacy settings block site-triggered install dialogs, so the icon is the reliable path.
              </li>
            </ol>
          )}

          {platform === 'brave-android' && (
            <ol className="space-y-3" data-testid="install-help-brave-android">
              <Step n="1">
                Tap the <Menu className="w-4 h-4 inline text-cyan-300 -mt-0.5" /> menu (3 dots, top-right).
              </Step>
              <Step n="2">
                Tap <span className="text-white font-medium">Add to Home screen</span> or{' '}
                <span className="text-white font-medium">Install app</span>.
              </Step>
            </ol>
          )}

          {/* Firefox desktop — no PWA support, give a real alternative. */}
          {platform === 'firefox' && (
            <div className="space-y-3" data-testid="install-help-firefox">
              <p className="text-white/70 text-sm leading-relaxed">
                Firefox doesn’t support installing web apps. For the full standalone-window
                experience, open NeuroWave in <span className="text-white font-medium">Chrome</span>,{' '}
                <span className="text-white font-medium">Edge</span>, or{' '}
                <span className="text-white font-medium">Brave</span>.
              </p>
              <p className="text-white/55 text-xs">
                You can still use NeuroWave in Firefox — just not as an installed app.
              </p>
            </div>
          )}

          {platform === 'firefox-android' && (
            <div className="space-y-3" data-testid="install-help-firefox-android">
              <p className="text-white/70 text-sm leading-relaxed">
                Firefox on Android doesn’t install PWAs. Open NeuroWave in{' '}
                <span className="text-white font-medium">Chrome</span> or{' '}
                <span className="text-white font-medium">Brave</span> to install.
              </p>
            </div>
          )}

          {platform === 'unknown' && (
            <p className="text-white/55 text-sm" data-testid="install-help-unknown">
              Looking up the right instructions for your browser…
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InstallHelpModal;
